import { attr, customElement, DOM, FASTElement, observable } from '@microsoft/fast-element';
import { Labels, vppaModaltabs, VppaModalTemplate as template } from './vppa-modal.template';
import { VppaModalStyles as styles } from './vppa-modal.styles';
import { FileUpload } from '../../common/file-upload/file-upload';
import { IVppaService } from '../../../services/vppa.service';
import { FieldValidation, IVppaInsert, ObjectValidation, ValidationError } from '../../../types';
import { VppaForm } from './vppa-form/vppa-form';
import { RightSideModal } from '../../right-side-modal/right-side-modal';
import { logger } from '@genesislcap/foundation-comms';
import { ValidationColumnConfig } from '../../common/validation-grid/validation-grid';
import { notify, notifyError } from '../../../utils/notify';
import { CONTRACT_TYPE } from '../../../utils';

const name = 'vppa-modal';

export enum vppaFieldsType {
  PROJECT_ID = 'string',
  VPPA_STATUS = 'string',
  CONTRACT_TYPE = 'string',
  OFFERED_CAPACITY = 'number',
  FIXED_PRICE = 'number',
  TENOR_START_DATE = 'date',
  TENOR_MONTHS = 'number',
  SETTLEMENT_POINT = 'string',
  SETTLEMENT_INDEX = 'string',
  HUB_ZONE_MATRIX_ID = 'string',
  REC_DELIVERY_METHODOLOGY = 'string',
  PERFORMANCE_GUARANTEE_METHOD = 'string',
  BUYERS_FRACTION = 'number',
  VPPA_CREDIT_PROFILE = 'string',
  SELLER_POST_COD_COLLATERAL = 'number',
  NEGATIVE_PRICING_METHOD = 'string',
  APPLICABLE_REC_PROGRAM = 'string',
  VPPA_REFERENCE = 'string',
  PRODUCT_TYPE = 'string',
}

@customElement({
  name,
  template,
  styles,
})
export class VppaModal extends FASTElement {
  @IVppaService vppaService: IVppaService;
  @attr show: boolean = false;
  @observable selectedTab: vppaModaltabs = vppaModaltabs.FORM;
  @observable title: Labels | string = Labels.TITLE;
  @observable isFileConfirmationState: boolean = false;
  @observable width: string = '375px';
  @observable file: any = undefined;
  @observable isFormValid: boolean;
  @observable isUpdateModal: boolean = false;

  @observable correctVppaObjectsUploaded: Array<ObjectValidation> = [];
  @observable incorrectVppaObjectsUploaded: Array<ObjectValidation> = [];
  @observable vppaColumns: Array<ValidationColumnConfig> = [];

  @observable vppaForm: VppaForm;
  @observable vppaFormUpdate: VppaForm;
  @observable vppaModal: RightSideModal;
  @observable selectedContractType: string;
  @observable selectedContractTicker: string;

  public fileUpload: FileUpload;

  constructor() {
    super();
    this.loadColumns();
  }

  private loadColumns() {
    this.vppaColumns.push({ fieldName: 'TICKER', headerName: 'Ticker', flex: 3 });
    this.vppaColumns.push({ fieldName: 'PROJECT_TECHNOLOGY', headerName: 'Type', flex: 2 });
    this.vppaColumns.push({
      fieldName: 'SETTLEMENT_POINT',
      headerName: 'Settlement Point',
      flex: 3,
    });
    this.vppaColumns.push({ fieldName: 'TENOR_START_DATE', headerName: 'COD', flex: 3 });
    this.vppaColumns.push({ fieldName: 'FIXED_PRICE', headerName: 'Price', flex: 3 });
  }

  public async openModal(isUpdate: boolean, vppaId: string, projectId: string, ticker?: string) {
    this.isUpdateModal = isUpdate;
    this.show = true;
    ticker ? (this.selectedContractTicker = ticker) : null;
    setTimeout(() => {
      if (isUpdate) {
        // Update Existing Contract
        this.title = Labels.UPDATE_TITLE + ' ' + ticker;
        this.vppaFormUpdate?.loadData(vppaId, null, isUpdate);
      } else {
        if (vppaId) {
          // Copy Data from an existing Contract
          this.title = Labels.COPY_TITLE + ' ' + ticker;
          this.vppaForm?.loadData(vppaId, null, isUpdate);
        } else {
          // New Contract
          this.title = Labels.ADD_BUTTON;
          this.vppaForm?.loadData(null, projectId, isUpdate);
        }
      }
    }, 100);
  }

  public selectTab(tab: vppaModaltabs) {
    this.selectedTab = tab;
  }

  closeModal() {
    this.title = Labels.TITLE;
    this.width = '375px';
    this.fileUpload?.cleanData();
    this.isFileConfirmationState = false;
    this.file = undefined;
    this.correctVppaObjectsUploaded?.splice(0);
    this.incorrectVppaObjectsUploaded?.splice(0);
    this.vppaForm.clearForm();
    this.vppaFormUpdate?.clearForm();
    this.isUpdateModal = false;
    this.show = false;
  }

  clearForm(): any {
    this.vppaForm.cleanClassValues();
    this.vppaFormUpdate?.reloadVppaData();
  }

  public submitForm() {
    if (this.isUpdateModal) {
      const formData = this.vppaFormUpdate.getFullFormData();

      this.vppaService.eventVppaUpdate(formData).then(
        (response) => {
          if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
            this.vppaModal.closeDialog();
          } else {
            notifyError(response.ERROR[0].TEXT);
            logger.error(response);
          }
        },
        (error) => {
          notifyError(error);
          logger.error(error);
        },
      );
    } else {
      const formData = {
        ...this.vppaForm.getFormData(),
      };

      this.vppaService.eventVppaInsert(formData).then(
        (response) => {
          if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
            this.vppaModal.closeDialog();
          } else {
            notifyError(response.ERROR[0].TEXT);
            logger.error(response);
          }
        },
        (error) => {
          notifyError(error);
          logger.error(error);
        },
      );
    }
  }

  public async submitFile() {
    this.fileUpload.setEndpoint('file-handler/upload-vppa');

    if (this.file) {
      const response = await this.vppaService.sendVppaCsv(
        this.file,
        this.fileUpload.getEndpointUrl(this.fileUpload.endpoint),
      );

      this.correctVppaObjectsUploaded = response.objects?.filter(
        (object) => !object.objectHasProblem,
      );
      this.incorrectVppaObjectsUploaded = response.objects?.filter(
        (object) => object.objectHasProblem,
      );
      this.isFileConfirmationState = true;
      this.width = '525px';
      this.title = Labels.CONFIRM_VPPA_UPLOAD_TITLE;
    }
  }

  public async insertVppasList() {
    if (this.isFileConfirmationState && !this.incorrectVppaObjectsUploaded.length) {
      const vppaResponseList = [];
      this.correctVppaObjectsUploaded.forEach((vppa) => {
        const vppaResponse: IVppaInsert = {
          PROJECT_ID: '',
          VPPA_STATUS: '',
          CONTRACT_TYPE: '',
          OFFERED_CAPACITY: 0,
          FIXED_PRICE: 0,
          TENOR_START_DATE: 0,
          TENOR_MONTHS: 0,
          SETTLEMENT_POINT: '',
          SETTLEMENT_INDEX: '',
          HUB_ZONE_MATRIX_ID: '',
          REC_DELIVERY_METHODOLOGY: '',
          PERFORMANCE_GUARANTEE_METHOD: '',
          BUYERS_FRACTION: 0,
          VPPA_CREDIT_PROFILE: '',
          SELLER_POST_COD_COLLATERAL: 0,
          NEGATIVE_PRICING_METHOD: '',
          APPLICABLE_REC_PROGRAM: '',
          VPPA_LEGAL_ENTITY: '',
          VPPA_REFERENCE: '',
          PRODUCT_TYPE: '',
          CASCADE_COMPANY_ID: '',
        };
        vppa.fields.map((field) => {
          return this.updateProjectField(field, vppaResponse);
        });
        vppaResponseList.push(vppaResponse);
      });

      this.vppaService.eventVppasInsert(vppaResponseList).then(
        (response) => {
          if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
            this.vppaModal.closeDialog();
          } else {
            notifyError(response.ERROR[0].TEXT);
            logger.error(response.ERROR);
          }
        },
        (error) => {
          notifyError(error);
          logger.error(error);
        },
      );
    }
  }

  updateProjectField(field: FieldValidation, vppaResponse: IVppaInsert) {
    if (vppaFieldsType[field.fieldName] === 'string') {
      vppaResponse[field.fieldName] = field.fieldValue;
    } else if (vppaFieldsType[field.fieldName] === 'number') {
      vppaResponse[field.fieldName] = Number(field.fieldValue);
    } else if (vppaFieldsType[field.fieldName] === 'date') {
      vppaResponse[field.fieldName] = new Date(field.fieldValue);
    }
  }

  public cancelFileUpload() {
    this.closeModal();
    this.vppaModal.closeDialog();
  }

  public clearFileArea() {
    if (this.file) {
      this.fileUpload?.cleanData();
      this.file = undefined;
    }
  }

  public fileSelected(file: any) {
    this.file = file;
    if (this.isFileConfirmationState) {
      this.submitFile();
    }
  }
}
